// Знакомство (D, решение владельца 2026-10-19): экран «что это + твой модуль» → настоящая тренировка
// на 5 слов без финального матча → итог (результат, 1-й день серии, время напоминаний, почта) →
// главная. Здесь — разбор ответов API и всё, что не зависит от React.
//
// Модуль без импортов приложения: его проверяет tests/onboarding.test.ts под Node.

/** Сколько карточек в первой тренировке — число на кнопке «Начать: 5 слов за 2 минуты». */
export const FIRST_TRAINING_SIZE = 5;

/** Ввод перевода и выбор перевода — как пресет «Разминка» конструктора (builderConfig). */
export const FIRST_TRAINING_EXERCISES = ['input', 'mcq'] as const;

// Страницы лендинга про карточки: генератор, сравнения с Anki и Quizlet, статьи блога о карточках и списках слов.
const CARDS_PAGE = /^\/(?:ai-kartochki|anki-alternative|quizlet-alternative)(?:\/|$)/;
const CARDS_POST = /^\/blog\/[^/]*(?:kartoch|anki|quizlet|spisok-slov|slova-iz-knigi)/;

/**
 * Шёл за карточками: на лендинге был на странице про карточки. Тогда на «С чего начнём?» первой стоит «Свои слова»
 * (решение владельца 2026-11-02: пришедшие из поиска на /ai-kartochki бросали тренировку готовой темы).
 */
export function isCardsLandingPath(path: string | null | undefined): boolean {
  return !!path && (CARDS_PAGE.test(path) || CARDS_POST.test(path));
}

/** Часы напоминаний по МСК на итоге знакомства; сервер принимает 7–22. */
export const REMINDER_HOURS = [8, 13, 19, 21] as const;

/** Час, который сервер берёт при `reminder_hour = null`. */
export const DEFAULT_REMINDER_HOUR = 19;

/** Шаги знакомства — значение `step` в событиях `onboarding_step_viewed` и `onboarding_skipped`. */
export type OnboardingStep = 'intro' | 'training' | 'result';

/** Выбор на итоге: час, «не надо» или null — ученик не трогал выбор (останется 19:00). */
export type ReminderChoice = number | 'off' | null;

/** Поля `finish-onboarding` про напоминания (тело — snake_case, как принимает бэкенд). */
export interface ReminderFields {
  reminder_hour?: number;
  reminders_off?: true;
}

export function reminderFields(choice: ReminderChoice): ReminderFields {
  if (choice === 'off') return { reminders_off: true };
  if (typeof choice === 'number' && Number.isInteger(choice) && choice >= 7 && choice <= 22) {
    return { reminder_hour: choice };
  }
  return {};
}

/**
 * Тот же выбор полями `PUT /profile` — когда знакомство уже засчитано при входе на итог (B5): второй
 * finish-onboarding ответил бы 409 и выбор потерялся бы. null — сохранять нечего (останется 19:00).
 */
export function reminderProfileFields(
  choice: ReminderChoice,
): { reminder_hour: number } | { is_notification: false } | null {
  if (choice === 'off') return { is_notification: false };
  const fields = reminderFields(choice);
  return fields.reminder_hour !== undefined ? { reminder_hour: fields.reminder_hour } : null;
}

/** Свойства события `reminder_time_selected`; null — ученик ничего не выбирал, событие не шлём. */
export function reminderEventProps(choice: ReminderChoice): { hour: number } | { off: true } | null {
  if (choice === 'off') return { off: true };
  if (typeof choice === 'number') return { hour: choice };
  return null;
}

/** Часовой пояс браузера совпадает с московским (UTC+3): подпись «время московское» не нужна. */
export function isMoscowOffset(offsetMinutes: number = new Date().getTimezoneOffset()): boolean {
  return offsetMinutes === -180;
}

// ---- Стартовый модуль ----

/** Официальный модуль Lomora, который знакомство предлагает добавить. */
export interface StarterModule {
  id: string;
  name: string;
  cefrLevel: string | null;
  wordCount: number;
}

export interface OnboardingStarter {
  /** Закреплённый модуль (ONBOARDING_STARTER_SLUG); null — сервер его не отдал. */
  starter: StarterModule | null;
  /** «Другая тема»: общие темы без IT, без самого стартового модуля. */
  alternatives: StarterModule[];
}

/** Копия модуля в аккаунте ученика (ответ `POST /onboarding/start`). */
export interface StartedModule {
  moduleId: string;
  /** Официальный модуль, с которого сделана копия. */
  sourceId: string | null;
  name: string;
  wordCount: number;
}

type Raw = Record<string, unknown>;

const isRecord = (value: unknown): value is Raw => !!value && typeof value === 'object' && !Array.isArray(value);

const count = (value: unknown): number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;

/** Ответы API приходят в snake_case — их приводит интерцептор `shared/api` (cefrLevel → cefr_level). */
export function parseStarterModule(raw: unknown): StarterModule | null {
  if (!isRecord(raw) || typeof raw.id !== 'string' || !raw.id || typeof raw.name !== 'string' || !raw.name) {
    return null;
  }
  return {
    id: raw.id,
    name: raw.name,
    cefrLevel: typeof raw.cefr_level === 'string' && raw.cefr_level ? raw.cefr_level : null,
    wordCount: count(raw.word_count),
  };
}

export function parseOnboardingStarter(raw: unknown): OnboardingStarter {
  const data = isRecord(raw) ? raw : {};
  const starter = parseStarterModule(data.starter);
  const seen = new Set(starter ? [starter.id] : []);
  const alternatives: StarterModule[] = [];
  for (const item of Array.isArray(data.alternatives) ? data.alternatives : []) {
    const module = parseStarterModule(item);
    if (!module || seen.has(module.id)) continue;
    seen.add(module.id);
    alternatives.push(module);
  }
  return { starter, alternatives };
}

export function parseStartedModule(raw: unknown): StartedModule | null {
  if (!isRecord(raw) || typeof raw.module_id !== 'string' || !raw.module_id) return null;
  return {
    moduleId: raw.module_id,
    sourceId: typeof raw.source_id === 'string' && raw.source_id ? raw.source_id : null,
    name: typeof raw.name === 'string' ? raw.name : '',
    wordCount: count(raw.word_count),
  };
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isUuid(value: unknown): value is string {
  return typeof value === 'string' && UUID.test(value);
}

// ---- Возврат посреди первой тренировки (B5, 2026-10-20) ----

/** Нужное из незаконченной тренировки (`GET /sessions/active`). */
export interface ActiveTrainingRef {
  status: string;
  cramMode: string | null;
  moduleId: string | null;
}

/** Модуль тренировки знакомства, в которую стоит вернуть ученика, или null: тренировка из
 *  конструктора (знакомство стартует `builder`), не законченная и с модулем. */
export function resumableTrainingModule(session: ActiveTrainingRef | null | undefined): string | null {
  if (!session || session.status !== 'in_progress' || session.cramMode !== 'builder') return null;
  return isUuid(session.moduleId) ? session.moduleId : null;
}

// Ученик ушёл с тренировки знакомства в этой вкладке («Назад», «Выйти», конец). Тогда экран знакомства
// не перекидывает его обратно в тренировку — иначе «Назад» стал бы ловушкой; «Начать» продолжит её.
// Перезагрузка и новая вкладка флаг сбрасывают: тогда возвращаем сразу в тренировку.
let trainingLeft = false;

export function markTrainingLeft(): void {
  trainingLeft = true;
}

export function trainingLeftInThisTab(): boolean {
  return trainingLeft;
}

// ---- Итог первой тренировки ----

/** Итог по карточкам — та же форма, что TrainingResult тренировки. */
export interface FirstTrainingResult {
  correct: number;
  total: number;
  answered: number;
}

/** sessionStorage: итог переживает перезагрузку экрана итога в этой вкладке. */
export const FIRST_TRAINING_KEY = 'lx.onboarding.firstTraining';
export const FIRST_TRAINING_TTL_MS = 6 * 60 * 60 * 1000;

export type KeyValueStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function sessionStore(): KeyValueStore | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null; // хранилище заблокировано настройками приватности
  }
}

const isCount = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 1000;

export function parseTrainingResult(value: unknown): FirstTrainingResult | null {
  if (!isRecord(value)) return null;
  const { correct, total, answered } = value;
  if (!isCount(correct) || !isCount(total) || !isCount(answered)) return null;
  if (total === 0 || correct > answered || answered > total) return null;
  return { correct, total, answered };
}

/** Меньше 60% с первой попытки (при 5 словах — 2 и меньше): итог не счётом «2 из 5», а «5 новых слов
 *  в твоём модуле · 2 — с первого раза» — первая тренировка не экзамен (решение владельца 2026-10-19). */
export function isLowResult(result: FirstTrainingResult): boolean {
  return result.total > 0 && result.correct / result.total < 0.6;
}

/** Все карточки — верно с первой попытки: на итоге «5 из 5!» с восклицательным знаком. */
export function isPerfectResult(result: FirstTrainingResult): boolean {
  return result.total > 0 && result.correct === result.total;
}

export function saveTrainingResult(
  result: FirstTrainingResult,
  store: KeyValueStore | null = sessionStore(),
  now: number = Date.now(),
): void {
  if (!store || !parseTrainingResult(result)) return;
  try {
    store.setItem(FIRST_TRAINING_KEY, JSON.stringify({ ...result, at: now }));
  } catch {
    // нет места — итог возьмём из состояния навигации
  }
}

export function readTrainingResult(
  store: KeyValueStore | null = sessionStore(),
  now: number = Date.now(),
): FirstTrainingResult | null {
  if (!store) return null;
  try {
    const raw = store.getItem(FIRST_TRAINING_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    const at = isRecord(parsed) ? parsed.at : undefined;
    if (typeof at !== 'number' || now - at > FIRST_TRAINING_TTL_MS || at - now > 60_000) {
      store.removeItem(FIRST_TRAINING_KEY);
      return null;
    }
    return parseTrainingResult(parsed);
  } catch {
    return null;
  }
}

export function clearTrainingResult(store: KeyValueStore | null = sessionStore()): void {
  try {
    store?.removeItem(FIRST_TRAINING_KEY);
  } catch {
    // нечего чистить
  }
}

/**
 * Итог из состояния навигации экрана итога. `undefined` — состояния нет (открыли адрес заново),
 * тогда итог берётся из sessionStorage; `null` — тренировку не закончили (вышли), цифр не показываем.
 */
export function resultFromNavigationState(state: unknown): FirstTrainingResult | null | undefined {
  if (!isRecord(state) || !('result' in state)) return undefined;
  return parseTrainingResult(state.result);
}
